import { Button, Descriptions, theme } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { VendorDetailResponse } from '@/types/api';
import { formatPhoneFromApiForDisplay } from '@/utils/phoneUtils';
import { formatAddressWithResolvedNames } from '@/utils/locationUtils';
import { AddressParseFailedTag } from '@/components/common/AddressParseFailedTag';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type { CountryInfo, StateInfo, CityInfo } from '@/types/api';

interface PrimaryInfoSectionProps {
  vendor: VendorDetailResponse;
  canEdit: boolean;
  onEditBasicInfo: () => void;
  countries: CountryInfo[];
  states: StateInfo[];
  cities: CityInfo[];
  // Children slots for ContactsSection and LocationsSection
  contactsSection: React.ReactNode;
  locationsSection: React.ReactNode;
}

const PrimaryInfoSection = ({
  vendor,
  canEdit,
  onEditBasicInfo,
  countries,
  states,
  cities,
  contactsSection,
  locationsSection,
}: PrimaryInfoSectionProps) => {
  const { t } = useTranslation('vendors');
  const { token } = theme.useToken();
  const elevation = useElevation();

  return (
    <div>
      {/* Basic Information */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('vendor.sections.basicInformation')}
        </h3>
        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={!canEdit}
          onClick={onEditBasicInfo}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        >
          {t('vendor.actions.edit')}
        </Button>
      </div>
      <div
        style={{
          backgroundColor: token.colorBgContainer,
          overflow: 'hidden',
          marginBottom: '24px',
          ...elevation.card,
        }}
      >
        <Descriptions bordered column={2} size="middle">
          <Descriptions.Item label={t('vendor.fields.primaryPort')}>{vendor.primaryPort || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('vendor.fields.salesRep')}>{vendor.salesRepresentativeIds?.join(', ') || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('vendor.fields.email')}>{vendor.email || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('vendor.fields.website')}>{vendor.web || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('vendor.fields.phone')}>
            {formatPhoneFromApiForDisplay(
              {
                phone: vendor.phone,
                phoneCountryCode: vendor.phoneCountryCode,
                phoneNumber: vendor.phoneNumber,
                phoneExtension: vendor.phoneExtension,
              },
              t('vendor.fields.ext')
            ) || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('vendor.fields.fax')}>{vendor.fax || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('vendor.fields.registeredAddress')} span={2}>
            {formatAddressWithResolvedNames(
              {
                address: vendor.address,
                cityId: vendor.cityId,
                stateId: vendor.stateId,
                postcode: vendor.postcode,
                countryId: vendor.countryId,
              },
              countries,
              states,
              cities
            )}
            {/* #101 BE-03: unparseable address → visible warning, not a silent dash */}
            {vendor.parseStatus === 'failed' && <AddressParseFailedTag />}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Contact Table Section */}
      {contactsSection}

      {/* Location Table Section */}
      {locationsSection}
    </div>
  );
};

export default PrimaryInfoSection;
